/**
 * Display names for dashboard location cards.
 *
 * Location cards are grouped and labelled by `Worksite.city`, which the backend
 * fills from the geocoding provider (Worksite.save() -> geocode() sets every
 * field the provider returns). The card UI renders that city alone and never
 * shows `state`, even though the payload carries it -- so a card can read
 * "Jamaica" (JFK's postal city, Queens NY) and be mistaken for the country, or
 * read "Flushing" and mean nothing to the viewer.
 *
 * Two things happen here:
 *
 *  1. The state is appended, so every card reads "Place, ST". This is the
 *     general fix and is what disambiguates Jamaica, NY from Jamaica.
 *  2. Airports whose postal place name is not what anyone says out loud get an
 *     explicit label, keyed on the backend's own `city|state|country` key so it
 *     can never collide with a same-named city in another state.
 *
 * DEMO-ONLY: this file lives on the staging-demo branch and ships to
 * demo.fleet-staging.com. It is a presentation override, not a data fix --
 * app.fleet-staging.com still shows the bare geocoded city.
 */

const LOCATION_DISPLAY_NAMES: Record<string, string> = {
  'Jamaica|NY|US': 'JFK',
  'Flushing|NY|US': 'LGA',
}

/**
 * Preferred label for a location: "Place, ST", with an airport name substituted
 * for the geocoded city where one applies.
 *
 * @param locationKey the backend's `city|state|country` key
 * @param city the geocoded city
 * @param state the geocoded state or region code
 */
const locationDisplayName = (
  locationKey: string | null | undefined,
  city: string | null | undefined,
  state?: string | null
): string => {
  const base =
    (locationKey ? LOCATION_DISPLAY_NAMES[locationKey] : undefined) ?? city ?? ''

  if (!base) {
    return ''
  }
  return state ? `${base}, ${state}` : base
}

export default locationDisplayName
